import {
  Box,
  Circle2d,
  Polygon2d,
  Polyline2d,
  SVGContainer,
  ShapeUtil,
  SvgExportContext,
  TLDefaultColorStyle,
  TLDefaultColorTheme,
  TLDefaultFillStyle,
  TLDrawShapeSegment,
  TLResizeInfo,
  TLShapeUtilCanvasSvgDef,
  VecLike,
  drawShapeProps,
  lerp,
  rng,
  toFixed,
  useEditor,
  useSvgExportContext,
  useValue,
  DefaultFontStyle,
  tlenv,
  Vec,
  precise,
  average,
  toDomPrecision,
  DefaultColorThemePalette,
  suffixSafeId,
  SvgExportDef,
  TLDefaultSizeStyle,
  useDefaultColorTheme,
  useSharedSafeId,
  useUniqueSafeId,
  TLDefaultDashStyle,
  TLBaseShape,
  T,
  RecordProps,
} from "@tldraw/tldraw";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getFreehandOptions, getPointsFromSegments, StrokeOptions } from "./getPath";
import { getStrokePoints } from "../../newGeoStrokePoints";
import { getSvgPathFromStrokePoints } from "../../newSVG";
import { getStrokeOutlineTracks, interpolateSegments, setStrokePointRadii } from "./HighlightShapeUtil";
import { StrokePoint } from "../../newTypes";
import { mixColors } from "../CustomCloudUtil";
import { getDashArray } from "../../new-geo-shape-helpers";

export function last<T>(array: T[]): T | undefined {
  return array[array.length - 1];
}

interface PatternDef {
  zoom: number;
  url: string;
  theme: "light" | "dark";
}

function HashPatternForExport() {
  const getHashPatternZoomName = useGetHashPatternZoomName();
  const maskId = useUniqueSafeId();
  const theme = useDefaultColorTheme();
  const t = 8 / 12;
  return (
    <>
      <mask id={maskId}>
        <rect x="0" y="0" width="8" height="8" fill="white" />
        <g strokeLinecap="round" stroke="black">
          <line x1={t * 1} y1={t * 3} x2={t * 3} y2={t * 1} />
          <line x1={t * 5} y1={t * 7} x2={t * 7} y2={t * 5} />
          <line x1={t * 9} y1={t * 11} x2={t * 11} y2={t * 9} />
        </g>
      </mask>
      <pattern id={getHashPatternZoomName(1, theme.id)} width="8" height="8" patternUnits="userSpaceOnUse">
        <rect x="0" y="0" width="8" height="8" fill={theme.solid} mask={`url(#${maskId})`} />
      </pattern>
    </>
  );
}

const canvasBlob = (size: [number, number], fn: (ctx: CanvasRenderingContext2D) => void) => {
  const canvas = document.createElement("canvas");
  canvas.width = size[0];
  canvas.height = size[1];
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  fn(ctx);
  return canvas.toDataURL();
};
let defaultPixels: { white: string; black: string } | null = null;
function getDefaultPixels() {
  if (!defaultPixels) {
    defaultPixels = {
      white: canvasBlob([1, 1], (ctx) => {
        ctx.fillStyle = "#f8f9fa";
        ctx.fillRect(0, 0, 1, 1);
      }),
      black: canvasBlob([1, 1], (ctx) => {
        ctx.fillStyle = "#212529";
        ctx.fillRect(0, 0, 1, 1);
      }),
    };
  }
  return defaultPixels;
}

function getPatternLodsToGenerate(maxZoom: number) {
  const levels: number[] = [];
  const minLod = 0;
  const maxLod = getPatternLodForZoomLevel(maxZoom);
  for (let i = minLod; i <= maxLod; i++) {
    levels.push(Math.pow(2, i));
  }
  return levels;
}

function getDefaultPatterns(maxZoom: number): PatternDef[] {
  const defaultPixels = getDefaultPixels();
  return getPatternLodsToGenerate(maxZoom).flatMap((zoom) => [
    { zoom, url: defaultPixels.white, theme: "light" },
    { zoom, url: defaultPixels.black, theme: "dark" },
  ]);
}

const generateImage = (dpr: number, currentZoom: number, darkMode: boolean) => {
  return new Promise<Blob>((resolve, reject) => {
    const size = TILE_PATTERN_SIZE * currentZoom * dpr;

    const canvasEl = document.createElement("canvas");
    canvasEl.width = size;
    canvasEl.height = size;

    const ctx = canvasEl.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = darkMode ? DefaultColorThemePalette.darkMode.solid : DefaultColorThemePalette.lightMode.solid;
    ctx.fillRect(0, 0, size, size);

    // This essentially generates an inverse of the pattern we're drawing.
    ctx.globalCompositeOperation = "destination-out";

    ctx.lineCap = "round";
    ctx.lineWidth = 1.25 * currentZoom * dpr;

    const t = 8 / 12;
    const s = (v: number) => v * currentZoom * dpr;

    ctx.beginPath();
    ctx.moveTo(s(t * 1), s(t * 3));
    ctx.lineTo(s(t * 3), s(t * 1));

    ctx.moveTo(s(t * 5), s(t * 7));
    ctx.lineTo(s(t * 7), s(t * 5));

    ctx.moveTo(s(t * 9), s(t * 11));
    ctx.lineTo(s(t * 11), s(t * 9));
    ctx.stroke();

    canvasEl.toBlob((blob) => {
      //! remove " || debugFlags.throwToBlob.get()" from condition"
      if (!blob) {
        reject();
      } else {
        resolve(blob);
      }
    });
  });
};
const TILE_PATTERN_SIZE = 8;
function usePattern() {
  const editor = useEditor();
  const dpr = useValue("devicePixelRatio", () => editor.getInstanceState().devicePixelRatio, [editor]);
  const maxZoom = useValue("maxZoom", () => Math.ceil(last(editor.getCameraOptions().zoomSteps)!), [editor]);
  const [isReady, setIsReady] = useState(false);
  const [backgroundUrls, setBackgroundUrls] = useState<PatternDef[]>(() => getDefaultPatterns(maxZoom));
  const getHashPatternZoomName = useGetHashPatternZoomName();

  useEffect(() => {
    if (process.env.NODE_ENV === "test") {
      setIsReady(true);
      return;
    }

    const promise = Promise.all(
      getPatternLodsToGenerate(maxZoom).flatMap<Promise<PatternDef>>((zoom) => [
        generateImage(dpr, zoom, false).then((blob) => ({
          zoom,
          theme: "light",
          url: URL.createObjectURL(blob),
        })),
        generateImage(dpr, zoom, true).then((blob) => ({
          zoom,
          theme: "dark",
          url: URL.createObjectURL(blob),
        })),
      ])
    );

    let isCancelled = false;
    promise.then((urls) => {
      if (isCancelled) return;
      setBackgroundUrls(urls);
      setIsReady(true);
    });
    return () => {
      isCancelled = true;
      setIsReady(false);
      promise.then((patterns) => {
        for (const { url } of patterns) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [dpr, maxZoom]);

  const defs = (
    <>
      {backgroundUrls.map((item) => {
        const id = getHashPatternZoomName(item.zoom, item.theme);
        return (
          <pattern key={id} id={id} width={TILE_PATTERN_SIZE} height={TILE_PATTERN_SIZE} patternUnits="userSpaceOnUse">
            <image href={item.url} width={TILE_PATTERN_SIZE} height={TILE_PATTERN_SIZE} />
          </pattern>
        );
      })}
    </>
  );

  return { defs, isReady };
}

function findHtmlLayerParent(element: Element): HTMLElement | null {
  if (element.classList.contains("tl-html-layer")) return element as HTMLElement;
  if (element.parentElement) return findHtmlLayerParent(element.parentElement);
  return null;
}

function PatternFillDefForCanvas() {
  const editor = useEditor();
  const containerRef = useRef<SVGGElement>(null);
  const { defs, isReady } = usePattern();

  useEffect(() => {
    if (isReady && tlenv.isSafari) {
      const htmlLayer = findHtmlLayerParent(containerRef.current!);
      if (htmlLayer) {
        // Wait for `patternContext` to be picked up
        editor.timers.requestAnimationFrame(() => {
          htmlLayer.style.display = "none";

          // Wait for 'display = "none"' to take effect
          editor.timers.requestAnimationFrame(() => {
            htmlLayer.style.display = "";
          });
        });
      }
    }
  }, [editor, isReady]);

  return (
    <g ref={containerRef} data-testid={isReady ? "ready-pattern-fill-defs" : undefined}>
      {defs}
    </g>
  );
}

export function getFillDefForCanvas(): TLShapeUtilCanvasSvgDef {
  return {
    key: `${DefaultFontStyle.id}:pattern`,
    component: PatternFillDefForCanvas,
  };
}

export function getFillDefForExport(fill: TLDefaultFillStyle): SvgExportDef {
  return {
    key: `${DefaultFontStyle.id}:${fill}`,
    async getElement() {
      if (fill !== "pattern") return null;

      return <HashPatternForExport />;
    },
  };
}

export const STROKE_SIZES: Record<TLDefaultSizeStyle, number> = {
  s: 2,
  m: 3.5,
  l: 5,
  xl: 10,
};

export interface DrawShapeOptions {
  /**
   * The maximum number of points in a line before the draw tool will begin a new shape.
   * A higher number will lead to poor performance while drawing very long lines.
   */
  readonly maxPointsPerShape: number;
}

export type TLDrawShapeUtilProps = TLBaseShape<
  "customDraw",
  {
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    color: TLDefaultColorStyle;
    fill: TLDefaultFillStyle;
    dash: TLDefaultDashStyle;
    size: TLDefaultSizeStyle;
    segments: TLDrawShapeSegment[];
    isComplete: boolean;
    isClosed: boolean;
    isPen: boolean;
    scale: number;
    weight?: number;
    outlineColor: string;
    transparency: number;
    strokeDasharray?: string;
    strokeWidth: number;
    filterBlur?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isScale?: boolean;
    isShowIndicator: boolean;
  }
>;

export class DrawShapeUtil extends ShapeUtil<TLDrawShapeUtilProps> {
  static type = "customDraw" as const;
  static props: RecordProps<TLDrawShapeUtilProps> = {
    ...drawShapeProps,
    colorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColor: T.string,
    dash: T.any as unknown as RecordProps<TLDrawShapeUtilProps>["dash"],
    transparency: T.number,
    weight: T.optional(T.number),
    strokeDasharray: T.optional(T.string),
    strokeWidth: T.number,
    filterBlur: T.optional(T.number),
    borderWidth: T.optional(T.number),
    borderColor: T.optional(T.string),
    borderSolid: T.any as unknown as RecordProps<TLDrawShapeUtilProps>["borderSolid"],
    borderTopStyle: T.optional(T.string),
    borderBottomStyle: T.optional(T.string),
    borderLeftStyle: T.optional(T.string),
    borderRightStyle: T.optional(T.string),
    isShowBorderTop: T.optional(T.boolean),
    isShowBorderBottom: T.optional(T.boolean),
    isShowBorderLeft: T.optional(T.boolean),
    isShowBorderRight: T.optional(T.boolean),
    isCreatedFilterBlur: T.optional(T.boolean),
    filterBlurValueWhenHidden: T.optional(T.number),
    outShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    inShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    previousBorderColor: T.optional(T.string),
    isCreatedBorderItem: T.optional(T.boolean),

    outlineColorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    isHiddenLayerBlur: T.optional(T.boolean),
    isHiddenBorder: T.optional(T.boolean),
    isScale: T.optional(T.boolean),
    isShowIndicator: T.boolean,
  };

  options: DrawShapeOptions = {
    maxPointsPerShape: 600,
  };

  hideResizeHandles(shape: TLDrawShapeUtilProps) {
    return getIsDot(shape);
  }
  hideRotateHandle(shape: TLDrawShapeUtilProps) {
    return getIsDot(shape);
  }
  hideSelectionBoundsFg(shape: TLDrawShapeUtilProps) {
    return getIsDot(shape);
  }

  getDefaultProps(): TLDrawShapeUtilProps["props"] {
    return {
      colorMix: {
        "0": { color: "black", isHidden: false },
      },
      segments: [],
      fill: "none" as TLDefaultFillStyle,
      dash: "draw" as TLDefaultDashStyle,
      size: "m" as TLDefaultSizeStyle,
      isComplete: false,
      isClosed: false,
      isPen: false,
      scale: 1,
      color: "black" as TLDefaultColorStyle,
      outlineColor: "#000000",
      transparency: 1,
      strokeWidth: 0,
      filterBlur: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isScale: false,
      isShowIndicator: true,
    };
  }

  getGeometry(shape: TLDrawShapeUtilProps) {
    const points = getPointsFromSegments(shape.props.segments);

    // Use weight if available, otherwise use default size calculation
    const sw =
      shape.props.weight !== undefined
        ? shape.props.weight * shape.props.scale
        : (STROKE_SIZES[shape.props.size] + 1) * shape.props.scale;

    // A dot
    if (shape.props.segments.length === 1) {
      const box = Box.FromPoints(points);
      if (box.width < sw * 2 && box.height < sw * 2) {
        return new Circle2d({
          x: -sw,
          y: -sw,
          radius: sw,
          isFilled: true,
        });
      }
    }

    const strokePoints = getStrokePoints(points, getFreehandOptions(shape.props, sw, shape.props.isPen, true)).map(
      (p: any) => p.point
    );

    // A closed draw stroke
    if (shape.props.isClosed) {
      return new Polygon2d({
        points: strokePoints,
        isFilled: shape.props.fill !== "none",
      });
    }

    // An open draw stroke
    return new Polyline2d({
      points: strokePoints,
    });
  }

  component(shape: TLDrawShapeUtilProps) {
    // Subscribe only to the *boolean* "is this shape hovered" — see
    // CustomRectUtil. Avoids O(N) re-renders on every hover transition.
    const editor = this.editor;
    const isHovered = useValue("isHovered", () => editor.getHoveredShapeId() === shape.id, [editor, shape.id]);

    return (
      <SVGContainer style={{ opacity: shape.props.transparency }}>
        <DrawShapeSvg shape={shape} />
        {/* {isHovered && (
            <svg
              style={{
                fill: "none",
                stroke: HOVER_EFFECT.COLOR,
                position: "absolute",
                zIndex: "-1",
                top: 0,
                left: 0,
                pointerEvents: "none",
              }}
            >
              {this.indicator(shape)}
            </svg>
          )} */}
      </SVGContainer>
    );
  }

  indicator(shape: TLDrawShapeUtilProps) {
    const allPointsFromSegments = getPointsFromSegments(shape.props.segments);

    // Use weight if available, otherwise use default size calculation
    let sw =
      shape.props.weight !== undefined
        ? shape.props.weight * shape.props.scale
        : (STROKE_SIZES[shape.props.size] + 1) * shape.props.scale;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const forceSolid = useValue(
      "force solid",
      () => {
        const zoomLevel = this.editor.getZoomLevel();
        return zoomLevel < 0.5 && zoomLevel < 1.5 / sw;
      },
      [this.editor, sw]
    );

    if (!forceSolid && !shape.props.isPen && shape.props.dash === "draw" && allPointsFromSegments.length === 1) {
      sw += rng(shape.id)() * (sw / 6);
    }

    const showAsComplete = shape.props.isComplete || last(shape.props.segments)?.type === "straight";
    const options = getFreehandOptions(shape.props, sw, showAsComplete, true);
    const strokePoints = getStrokePoints(allPointsFromSegments, options);
    const solidStrokePath =
      strokePoints.length > 1
        ? getSvgPathFromStrokePoints(strokePoints, shape.props.isClosed)
        : getDot(allPointsFromSegments[0], sw);

    return !shape.props.isShowIndicator ? <path d={solidStrokePath} /> : null;
  }

  toSvg(shape: TLDrawShapeUtilProps, ctx: SvgExportContext) {
    ctx.addExportDef(getFillDefForExport(shape.props.fill));
    const scaleFactor = 1 / shape.props.scale;
    return (
      <g transform={`scale(${scaleFactor})`}>
        <DrawShapeSvg shape={shape} zoom={1} />
      </g>
    );
  }

  getCanvasSvgDefs(): TLShapeUtilCanvasSvgDef[] {
    return [getFillDefForCanvas()];
  }

  onResize(shape: TLDrawShapeUtilProps, info: TLResizeInfo<TLDrawShapeUtilProps>) {
    const { scaleX, scaleY } = info;

    const newSegments: TLDrawShapeSegment[] = [];

    const uniformScale = shape?.props?.isScale
      ? scaleX < 1 || scaleY < 1
        ? Math.min(scaleX, scaleY)
        : Math.max(scaleX, scaleY)
      : null;

    for (const segment of shape.props.segments) {
      newSegments.push({
        ...segment,
        points: segment.points.map(({ x, y, z }) => {
          const scale = uniformScale ?? { scaleX, scaleY };
          return {
            x: toFixed((uniformScale ?? scaleX) * x),
            y: toFixed((uniformScale ?? scaleY) * y),
            z,
          };
        }),
      });
    }

    return {
      props: {
        segments: newSegments,
      },
    };
  }

  expandSelectionOutlinePx(shape: TLDrawShapeUtilProps): number {
    const multiplier = shape.props.dash === "draw" ? 1.6 : 1;
    // Use weight if available, otherwise use default size calculation
    const baseSize = shape.props.weight !== undefined ? shape.props.weight : STROKE_SIZES[shape.props.size];
    return ((baseSize * multiplier) / 2) * shape.props.scale;
  }
  getInterpolatedProps(
    startShape: TLDrawShapeUtilProps,
    endShape: TLDrawShapeUtilProps,
    t: number
  ): TLDrawShapeUtilProps["props"] {
    return {
      ...(t > 0.5 ? endShape.props : startShape.props),
      segments: interpolateSegments(startShape.props.segments, endShape.props.segments, t),
      scale: lerp(startShape.props.scale, endShape.props.scale, t),
    };
  }
}

function getDot(point: VecLike, sw: number) {
  const r = (sw + 1) * 0.5;
  return `M ${point.x} ${point.y} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`;
}

function getIsDot(shape: TLDrawShapeUtilProps) {
  return shape.props.segments.length === 1 && shape.props.segments[0].points.length < 2;
}

function DrawShapeSvg({ shape, zoom }: { shape: TLDrawShapeUtilProps; zoom?: number }) {
  const theme = useDefaultColorTheme();
  const editor = useEditor();

  const visibleColors = Object.values(shape.props.colorMix).filter((c) => !c.isHidden);
  const calculatedFill = visibleColors.length > 0 ? mixColors(visibleColors.map((c) => c.color)) : "transparent";

  const allPointsFromSegments = getPointsFromSegments(shape.props.segments);

  const showAsComplete = shape.props.isComplete || last(shape.props.segments)?.type === "straight";

  // Use weight if available, otherwise use default size calculation
  let sw =
    shape.props.weight !== undefined
      ? shape.props.weight * shape.props.scale
      : (STROKE_SIZES[shape.props.size] + 1) * shape.props.scale;
  const forceSolid = useValue(
    "force solid",
    () => {
      const zoomLevel = zoom ?? editor.getZoomLevel();
      return zoomLevel < 0.5 && zoomLevel < 1.5 / sw;
    },
    [editor, sw, zoom]
  );

  const dotAdjustment = useValue(
    "dot adjustment",
    () => {
      const zoomLevel = zoom ?? editor.getZoomLevel();
      // If we're zoomed way out (10%), then we need to make the dotted line go to 9 instead 0.1
      // Chrome doesn't render anything otherwise.
      return zoomLevel < 0.2 ? 0 : 0.1;
    },
    [editor, zoom]
  );

  if (!forceSolid && !shape.props.isPen && shape.props.dash === "draw" && allPointsFromSegments.length === 1) {
    sw += rng(shape.id)() * (sw / 6);
  }

  const options = getFreehandOptions(shape.props, sw, showAsComplete, forceSolid);
  const {
    outlineColor,
    dash,
    transparency,
    strokeDasharray,
    strokeWidth,
    outShadowArray,
    inShadowArray,
    colorMix,
    outlineColorMix,
    isHiddenLayerBlur,
    isHiddenBorder,
  } = shape.props;

  const shadowFilterId = `shadow-${shape.id}`; // Unique ID for the shadow filter
  const innerShadowFilterId = `inner-shadow-${shape.id}`;
  const dashArray = strokeDasharray || getDashArray(dash);

  if (!forceSolid && shape.props.dash === "draw") {
    return (
      <>
        {shape.props.isClosed && shape.props.fill && allPointsFromSegments.length > 1 ? (
          <ShapeFill
            d={getSvgPathFromStrokePoints(getStrokePoints(allPointsFromSegments, options), shape.props.isClosed)}
            theme={theme}
            color={calculatedFill}
            // @ts-ignore - fill type mismatch between custom and tldraw ShapeFill
            fill={shape.props.isClosed ? shape.props.fill : "none"}
            scale={shape.props.scale}
          />
        ) : null}
        <defs>
          {/* Gradient definition */}
          {Object.values(colorMix).filter((c) => !c.isHidden).length > 0 && (
            <linearGradient id={`gradient-${shape.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
              {Object.values(colorMix)
                .filter((c) => !c.isHidden)
                .map((colorObj, index) => (
                  <stop
                    key={index}
                    offset={`${(index / (Object.values(colorMix).filter((c) => !c.isHidden).length - 1)) * 100}%`}
                    stopColor={colorObj.color}
                  />
                ))}
            </linearGradient>
          )}
          {/* Shadow filter definition */}
          {outShadowArray &&
            Object.values(outShadowArray).map((shadow) => (
              <>
                {!shadow.isHidden && (
                  <filter key={shadow.id} id={shadow.id} x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow
                      dx={shadow.dx}
                      dy={shadow.dy}
                      stdDeviation={shadow.shadowBlur}
                      floodColor={shadow.color}
                      floodOpacity={shadow.opacity}
                    />
                  </filter>
                )}
              </>
            ))}
          {/* Inner shadow filter definition */}
          {inShadowArray &&
            Object.values(inShadowArray).map((shadow) => (
              <>
                {!shadow.isHidden && (
                  <filter key={shadow.id} id={shadow.id} x="-50%" y="-50%" width="200%" height="200%">
                    <feComponentTransfer in="SourceAlpha">
                      <feFuncA type="table" tableValues="1 0" />
                    </feComponentTransfer>
                    <feGaussianBlur stdDeviation={shadow.shadowBlur} />
                    <feOffset dx={shadow.dx} dy={shadow.dy} result="offsetBlur" />
                    <feFlood floodColor={shadow.color} result="color" />
                    <feComposite in="color" in2="offsetBlur" operator="in" />
                    <feComponentTransfer>
                      <feFuncA type="linear" slope={shadow.opacity} />
                    </feComponentTransfer>
                    <feComposite in2="SourceAlpha" operator="in" />
                    <feComposite operator="over" in2="SourceGraphic" />
                  </filter>
                )}
              </>
            ))}
        </defs>
        <path
          filter={`url(#${shadowFilterId})`}
          d={svgInk(allPointsFromSegments, options)}
          strokeLinecap="round"
          fill={calculatedFill}
          stroke={outlineColor}
          strokeWidth={strokeWidth}
          strokeDasharray={dashArray}
          fillOpacity={transparency}
          strokeOpacity={transparency}
        />
      </>
    );
  }

  const strokePoints = getStrokePoints(allPointsFromSegments, options);
  const isDot = strokePoints.length < 2;
  const solidStrokePath = isDot
    ? getDot(allPointsFromSegments[0], 0)
    : getSvgPathFromStrokePoints(strokePoints, shape.props.isClosed);

  return (
    <>
      {/* <ShapeFill
        d={solidStrokePath}
        theme={theme}
        color={shape.props.color}
        fill={isDot || shape.props.isClosed ? shape.props.fill : "none"}
        scale={shape.props.scale}
      />

      <path
        d={solidStrokePath}
        strokeLinecap="round"
        fill={isDot ? theme[shape.props.color].solid : "none"}
        stroke={theme[shape.props.color].solid}
        strokeWidth={sw}
        strokeDasharray={isDot ? "none" : getDrawShapeStrokeDashArray(shape, sw, dotAdjustment)}
        strokeDashoffset="0"
      /> */}
    </>
  );
}

interface ShapeFillProps {
  d: string;
  fill: TLDefaultFillStyle;
  color: string;
  theme: TLDefaultColorTheme;
  scale: number;
}

const ShapeFill = React.memo(function ShapeFill({ theme, d, color, fill, scale }: ShapeFillProps) {
  switch (fill) {
    case "none": {
      return null;
    }
    case "solid": {
      return <path fill={"solid"} d={d} />;
    }
    case "semi": {
      return <path fill={theme.solid} d={d} />;
    }
    case "fill": {
      return <path fill={"solid"} d={d} />;
    }
    case "pattern": {
      return <PatternFill theme={theme} color={color} fill={fill} d={d} scale={scale} />;
    }
    default: {
      return null;
    }
  }
});

function getPatternLodForZoomLevel(zoom: number) {
  return Math.ceil(Math.log2(Math.max(1, zoom)));
}

export function useGetHashPatternZoomName() {
  const id = useSharedSafeId("hash_pattern");
  return useCallback(
    (zoom: number, theme: TLDefaultColorTheme["id"]) => {
      const lod = getPatternLodForZoomLevel(zoom);
      return suffixSafeId(id, `${theme}_${lod}`);
    },
    [id]
  );
}

export function PatternFill({ d, color, theme }: ShapeFillProps) {
  const editor = useEditor();
  const svgExport = useSvgExportContext();
  const zoomLevel = useValue("zoomLevel", () => editor.getZoomLevel(), [editor]);
  const getHashPatternZoomName = useGetHashPatternZoomName();

  const teenyTiny = editor.getZoomLevel() <= 0.18;

  return (
    <>
      <path fill={"solid"} d={d} />
      <path
        fill={
          svgExport
            ? `url(#${getHashPatternZoomName(1, theme.id)})`
            : teenyTiny
            ? color
            : `url(#${getHashPatternZoomName(zoomLevel, theme.id)})`
        }
        d={d}
      />
    </>
  );
}

////////////////////////////////////////////////////////
function partitionAtElbows(points: StrokePoint[]): StrokePoint[][] {
  if (points.length <= 2) return [points];

  function cleanUpPartition(partition: StrokePoint[]) {
    // clean up start of partition (remove points that are too close to the start)
    const startPoint = partition[0];
    let nextPoint: StrokePoint;
    while (partition.length > 2) {
      nextPoint = partition[1];
      if (Vec.Dist2(startPoint.point, nextPoint.point) < (((startPoint.radius + nextPoint.radius) / 2) * 0.5) ** 2) {
        partition.splice(1, 1);
      } else {
        break;
      }
    }
    // clean up end of partition in the same fashion
    const endPoint = partition[partition.length - 1];
    let prevPoint: StrokePoint;
    while (partition.length > 2) {
      prevPoint = partition[partition.length - 2];
      if (Vec.Dist2(endPoint.point, prevPoint.point) < (((endPoint.radius + prevPoint.radius) / 2) * 0.5) ** 2) {
        partition.splice(partition.length - 2, 1);
      } else {
        break;
      }
    }
    // now readjust the cap point vectors to point to their nearest neighbors
    if (partition.length > 1) {
      partition[0] = {
        ...partition[0],
        vector: Vec.Sub(partition[0].point, partition[1].point).uni(),
      };
      partition[partition.length - 1] = {
        ...partition[partition.length - 1],
        vector: Vec.Sub(partition[partition.length - 2].point, partition[partition.length - 1].point).uni(),
      };
    }
    return partition;
  }

  const result: StrokePoint[][] = [];
  let currentPartition: StrokePoint[] = [points[0]];
  let prevV = Vec.Sub(points[1].point, points[0].point).uni();
  let nextV: Vec;
  let dpr: number;
  let prevPoint: StrokePoint, thisPoint: StrokePoint, nextPoint: StrokePoint;
  for (let i = 1, n = points.length; i < n - 1; i++) {
    prevPoint = points[i - 1];
    thisPoint = points[i];
    nextPoint = points[i + 1];

    nextV = Vec.Sub(nextPoint.point, thisPoint.point).uni();
    dpr = Vec.Dpr(prevV, nextV);
    prevV = nextV;

    if (dpr < -0.8) {
      // always treat such acute angles as elbows
      // and use the extended .input point as the elbow point for swooshiness in fast zaggy lines
      const elbowPoint = {
        ...thisPoint,
        point: thisPoint.input,
      };
      currentPartition.push(elbowPoint);
      result.push(cleanUpPartition(currentPartition));
      currentPartition = [elbowPoint];
      continue;
    }
    currentPartition.push(thisPoint);

    if (dpr > 0.7) {
      // Not an elbow
      continue;
    }

    // so now we have a reasonably acute angle but it might not be an elbow if it's far
    // away from it's neighbors, angular dist is a normalized representation of how far away the point is from it's neighbors
    // (normalized by the radius)
    if (
      (Vec.Dist2(prevPoint.point, thisPoint.point) + Vec.Dist2(thisPoint.point, nextPoint.point)) /
        ((prevPoint.radius + thisPoint.radius + nextPoint.radius) / 3) ** 2 <
      1.5
    ) {
      // if this point is kinda close to its neighbors and it has a reasonably
      // acute angle, it's probably a hard elbow
      currentPartition.push(thisPoint);
      result.push(cleanUpPartition(currentPartition));
      currentPartition = [thisPoint];
      continue;
    }
  }
  currentPartition.push(points[points.length - 1]);
  result.push(cleanUpPartition(currentPartition));

  return result;
}

function circlePath(cx: number, cy: number, r: number) {
  return (
    "M " +
    cx +
    " " +
    cy +
    " m -" +
    r +
    ", 0 a " +
    r +
    "," +
    r +
    " 0 1,1 " +
    r * 2 +
    ",0 a " +
    r +
    "," +
    r +
    " 0 1,1 -" +
    r * 2 +
    ",0"
  );
}

function renderPartition(strokePoints: StrokePoint[], options: StrokeOptions = {}): string {
  if (strokePoints.length === 0) return "";
  if (strokePoints.length === 1) {
    return circlePath(strokePoints[0].point.x, strokePoints[0].point.y, strokePoints[0].radius);
  }

  const { left, right } = getStrokeOutlineTracks(strokePoints, options);
  right.reverse();
  let svg = `M${precise(left[0])}T`;

  // draw left track
  for (let i = 1; i < left.length; i++) {
    svg += average(left[i - 1], left[i]);
  }
  // draw end cap arc
  {
    const point = strokePoints[strokePoints.length - 1];
    const radius = point.radius;
    const direction = point.vector.clone().per().neg();
    const arcStart = Vec.Add(point.point, Vec.Mul(direction, radius));
    const arcEnd = Vec.Add(point.point, Vec.Mul(direction, -radius));
    svg += `${precise(arcStart)}A${toDomPrecision(radius)},${toDomPrecision(radius)} 0 0 1 ${precise(arcEnd)}T`;
  }
  // draw right track
  for (let i = 1; i < right.length; i++) {
    svg += average(right[i - 1], right[i]);
  }
  // draw start cap arc
  {
    const point = strokePoints[0];
    const radius = point.radius;
    const direction = point.vector.clone().per();
    const arcStart = Vec.Add(point.point, Vec.Mul(direction, radius));
    const arcEnd = Vec.Add(point.point, Vec.Mul(direction, -radius));
    svg += `${precise(arcStart)}A${toDomPrecision(radius)},${toDomPrecision(radius)} 0 0 1 ${precise(arcEnd)}Z`;
  }
  return svg;
}

export function svgInk(rawInputPoints: VecLike[], options: StrokeOptions = {}) {
  const { start = {}, end = {} } = options;
  const { cap: capStart = true } = start;
  const { cap: capEnd = true } = end;
  assert(!start.taper && !end.taper, "cap taper not supported here");
  assert(!start.easing && !end.easing, "cap easing not supported here");
  assert(capStart && capEnd, "cap must be true");

  const points = getStrokePoints(rawInputPoints, options);
  setStrokePointRadii(points, options);
  const partitions = partitionAtElbows(points);
  let svg = "";
  for (const partition of partitions) {
    svg += renderPartition(partition, options);
  }

  return svg;
}

export function assert(condition: any, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}
